import React, { useCallback, useMemo, useReducer } from 'react';

import * as http from '../../http';
import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPostRequest,
} from '../../http/request-templates';
import { ReportsContext } from './ReportsContext';
import { SavedFilter } from './ReportsModels';

import reportsReducer, {
  ERR_CREATE_SAVED_FILTERS,
  ERR_GET_SAVED_FILTERS,
  ERR_REMOVE_SAVED_FILTER,
  RCV_CREATE_SAVED_FILTERS,
  RCV_GET_SAVED_FILTERS,
  RCV_REMOVE_SAVED_FILTER,
  reportsReducerInitialState,
  REQ_CREATE_SAVED_FILTERS,
  REQ_GET_SAVED_FILTERS,
  REQ_REMOVE_SAVED_FILTER,
} from './reportsReducer';

import { createGuid } from '../../Utils/guid';
import useAuth from '../Auth/useAuth';

interface ReportsProviderProps {
  children: React.ReactNode;
}

const ReportsProvider = ({ children }: ReportsProviderProps) => {
  const { getAccessToken } = useAuth();

  const [reportsState, dispatch] = useReducer(reportsReducer, reportsReducerInitialState);

  const getSavedFilters = useCallback(async () => {
    try {
      dispatch({ type: REQ_GET_SAVED_FILTERS });
      const url = `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/templates/statistics`;
      const response = await http.rawJson<SavedFilter[]>(
        url,
        buildAuthorizedGetRequest(await getAccessToken())
      );
      dispatch({ type: RCV_GET_SAVED_FILTERS, savedFilters: response });
    } catch (e) {
      dispatch({ type: ERR_GET_SAVED_FILTERS });
    }
  }, [getAccessToken]);

  const createSavedFilter = useCallback(
    async (newSavedFilter: SavedFilter) => {
      try {
        dispatch({ type: REQ_CREATE_SAVED_FILTERS });

        const payload = { ...newSavedFilter, id: createGuid() };
        const url = `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/templates/statistics`;

        await http.rawJson<SavedFilter[]>(
          url,
          buildAuthorizedPostRequest(await getAccessToken(), payload)
        );

        dispatch({ type: RCV_CREATE_SAVED_FILTERS, savedFilter: payload });
      } catch (e) {
        dispatch({ type: ERR_CREATE_SAVED_FILTERS });
      }
    },
    [getAccessToken]
  );

  const removeSavedFilter = useCallback(
    async (id: string) => {
      try {
        dispatch({ type: REQ_REMOVE_SAVED_FILTER });
        await http.rawJson(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/templates/statistics/${id}`,
          buildAuthorizedDeleteRequest(await getAccessToken())
        );
        dispatch({ type: RCV_REMOVE_SAVED_FILTER, id });
      } catch (e) {
        dispatch({ type: ERR_REMOVE_SAVED_FILTER });
      }
    },
    [getAccessToken]
  );

  const value = useMemo(
    () => ({
      ...reportsState,
      getSavedFilters,
      createSavedFilter,
      removeSavedFilter,
    }),
    [createSavedFilter, getSavedFilters, removeSavedFilter, reportsState]
  );

  return <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>;
};

export default ReportsProvider;
